import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	expensesByReporting: [],
	expensesById: [],
	expenseStatusMaster: [],
	expenseHistory: null,
	total: 0,
	expense: null,
	loading: false,
	expenseStatusLoading: false,
	error: null,
};

const extractMessages = (data) => {
	if (Array.isArray(data)) {
		return data;
	}
	if (Array.isArray(data?.messages)) {
		return data.messages;
	}
	if (Array.isArray(data?.result)) {
		return data.result;
	}
	return null;
};

const showSuccessMessages = (data, fallbackMessage) => {
	const messages = extractMessages(data);
	if (messages && messages.length) {
		messages.forEach((msg) => {
			if (msg?.status === "success") {
				toast.success(msg?.message || fallbackMessage || "Operation successful");
			} else if (msg?.status === "error") {
				toast.error(msg?.message || "Operation failed");
			}
		});
	} else if (fallbackMessage) {
		toast.success(fallbackMessage);
	}
};

const showErrorMessages = (errorData, fallbackMessage) => {
	const messages = extractMessages(errorData);
	if (messages && messages.length) {
		messages.forEach((msg) => toast.error(msg?.message || fallbackMessage));
	} else {
		toast.error(errorData?.message || fallbackMessage);
	}
};

const normalizeNumber = (value, fallback = null) => {
	if (value === undefined || value === null || value === "") {
		return fallback;
	}
	const numeric = Number(value);
	return Number.isNaN(numeric) ? fallback : numeric;
};

const buildExpensePayload = (payload = {}) => ({
	ExpenseId: normalizeNumber(payload.ExpenseId, 0),
	EmployeeId: normalizeNumber(payload.EmployeeId, 0),
	ProjectId: normalizeNumber(payload.ProjectId, null),
	ExpenseTypeId: normalizeNumber(payload.ExpenseTypeId, 1),
	ExpenseDate: payload.ExpenseDate ?? null,
	Amount: normalizeNumber(payload.Amount, 0),
	ApprovedAmount: normalizeNumber(payload.ApprovedAmount, null),
	Remarks: payload.Remarks ?? null,
	ReceiptPath: payload.ReceiptPath ?? null,
	StatusId: normalizeNumber(payload.StatusId, 0),
	DataStatus: normalizeNumber(payload.DataStatus, 1),
});

export const loadExpenses = createAsyncThunk(
	"expense/loadAll",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("expenses/ExpenseDetails");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load expenses",
				}
			);
		}
	}
);

export const loadExpensesByReporting = createAsyncThunk(
	"expense/loadExpensesByReporting",
	async (employeeId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`expenses/ExpenseGetByReporting?EmployeeId=${encodeURIComponent(employeeId)}`
			);
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load expenses by reporting",
				}
			);
		}
	}
);

export const loadExpensesById = createAsyncThunk(
	"expense/loadExpensesById",
	async (employeeId, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(
				`expenses/ExpenseById?EmployeeId=${encodeURIComponent(employeeId)}`
			);
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load expenses by employee",
				}
			);
		}
	}
);

export const loadExpenseStatusMaster = createAsyncThunk(
	"expense/loadExpenseStatusMaster",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get("expenses/ExpanseStatusMaster");
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load expense status master",
				}
			);
		}
	}
);

export const expenseUpdateAction = createAsyncThunk(
	"expense/expenseUpdateAction",
	async ({ ExpenseId, StatusId }, { rejectWithValue }) => {
		try {
			const response = await axios.post("expenses/ExpenseUpdateAction", {
				ExpenseId: normalizeNumber(ExpenseId, 0),
				StatusId: normalizeNumber(StatusId, 0),
			});
			showSuccessMessages(
				response?.data,
				StatusId === 1 ? "Expense accepted" : "Expense rejected"
			);
			return response?.data;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(
				errorData,
				StatusId === 1 ? "Failed to accept expense" : "Failed to reject expense"
			);
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Expense action failed",
				}
			);
		}
	}
);

export const saveExpense = createAsyncThunk(
	"expense/save",
	async (payload, { rejectWithValue }) => {
		const requestBody = buildExpensePayload(payload);
		try {
			const response = await axios.post("expenses/ExpenseDetailsSetup", requestBody);
			const responseData = response?.data;
			const isDelete = requestBody.DataStatus === 2;
			showSuccessMessages(
				responseData,
				isDelete
					? "Expense deleted"
					: requestBody.ExpenseId && requestBody.ExpenseId !== 0
					? "Expense updated"
					: "Expense added"
			);
			return responseData;
		} catch (error) {
			const errorData = error?.response?.data;
			showErrorMessages(errorData, "Failed to save expense");
			return rejectWithValue(
				errorData ?? {
					message: error?.message || "Failed to save expense",
				}
			);
		}
	}
);

// Legacy endpoints for compatibility
export const addExpenseForm = createAsyncThunk(
	"expense/addExpenseForm",
	async (values) => {
		try {
			const { data } = await axios({
				method: "post",
				headers: {
					Accept: "application/json",
					"Content-Type": "multipart/form-data",
				},
				url: `expense-form/`,
				data: {
					...values,
				},
			});
			toast.success("Expense Created");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in creating expense, try again!");
			return {
				message: "error",
			};
		}
	}
);

export const reviewExpenseForm = createAsyncThunk(
	"expense/reviewExpenseForm",
	async ({ id, values }) => {
		try {
			const { data } = await axios({
				method: "put",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json;charset=UTF-8",
				},
				url: `expense-form/${id}`,
				data: {
					...values,
				},
			});

			toast.success(" Expense Approved");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in approving try again");
			return {
				message: "error",
			};
		}
	}
);

export const loadSingleExpenseForm = createAsyncThunk(
	"expense/loadSingleExpenseForm",
	async (id) => {
		try {
			const data = await axios.get(`expense-form/${id}`);
			return data;
		} catch (error) {
			return {
				message: error?.message || "Unable to load expense",
			};
		}
	}
);

export const loadExpenseFormByStatus = createAsyncThunk(
	"expense/loadExpenseFormByStatus",
	async ({ page, limit, approvalStatus }) => {
		try {
			const { data } = await axios.get(
				`expense-form?page=${page}&count=${limit}&approvalStatus=${approvalStatus}`
			);
			return data;
		} catch (error) {
			return {
				message: error?.message || "Unable to load expenses by status",
			};
		}
	}
);

export const loadAllExpenseForm = createAsyncThunk(
	"expense/loadAllExpenseForm",
	async () => {
		try {
			const { data } = await axios.get(`expense-form?query=all`);

			return data;
		} catch (error) {
			return {
				message: error?.message || "Unable to load expenses",
			};
		}
	}
);

export const countExpenseForm = createAsyncThunk(
	"expense/countExpenseForm",
	async () => {
		try {
			const { data } = await axios.get(`expense-form`);
			return data;
		} catch (error) {
			return {
				message: error?.message || "Unable to count expenses",
			};
		}
	}
);

export const loadSingleExpenseHistory = createAsyncThunk(
	"expense/loadSingleExpenseHistory",
	async (id) => {
		try {
			const { data } = await axios.get(`expense-form/expense-history/${id}/`);
			return data;
		} catch (error) {
			return {
				message: error?.message || "Unable to load expense history",
			};
		}
	}
);

export const updateExpenseForm = createAsyncThunk(
	"expense/updateExpenseForm",
	async ({ id, values }) => {
		try {
			const { data } = await axios({
				method: "put",
				url: `expense-form/${id}`,
				data: {
					...values,
				},
			});
			toast.success("Expense Updated");
			return {
				data,
				message: "success",
			};
		} catch (error) {
			toast.error("Error in updating Expense try again");
			return {
				message: "error",
			};
		}
	}
);

const expenseSlice = createSlice({
	name: "expense",
	initialState,
	reducers: {
		clearExpenseForm: (state) => {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadAllExpenseForm.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadAllExpenseForm.fulfilled, (state, action) => {
				state.loading = false;

				state.list = action.payload;
			})
			.addCase(loadAllExpenseForm.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(addExpenseForm.pending, (state) => {
				state.loading = true;
			})
			.addCase(addExpenseForm.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(addExpenseForm.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(loadSingleExpenseForm.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadSingleExpenseForm.fulfilled, (state, action) => {
				state.loading = false;
				state.expense = action.payload?.data ?? null;
			})
			.addCase(loadSingleExpenseForm.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(loadExpenseFormByStatus.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadExpenseFormByStatus.fulfilled, (state, action) => {
				state.loading = false;
				state.list = action.payload;
			})
			.addCase(loadExpenseFormByStatus.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(updateExpenseForm.pending, (state) => {
				state.loading = true;
			})
			.addCase(updateExpenseForm.fulfilled, (state, action) => {
				state.loading = false;
				const list = [...state.list];
				const updatedId = Number(action.payload?.data?.id ?? action.payload?.data?.ExpenseId);
				if (updatedId) {
					const index = list.findIndex(
						(expense) =>
							Number(expense?.id ?? expense?.ExpenseId ?? expense?.expenseId) === updatedId
					);
					if (index !== -1) {
						list[index] = action.payload.data;
						state.list = list;
					}
				}
			})
			.addCase(updateExpenseForm.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(reviewExpenseForm.pending, (state) => {
				state.loading = true;
			})
			.addCase(reviewExpenseForm.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(reviewExpenseForm.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(countExpenseForm.pending, (state) => {
				state.loading = true;
			})
			.addCase(countExpenseForm.fulfilled, (state, action) => {
				state.loading = false;
				const totalCount = Array.isArray(action.payload)
					? action.payload.length
					: Number(action.payload?.total ?? action.payload ?? 0);
				state.total = Number.isFinite(totalCount) ? totalCount : 0;
			})
			.addCase(countExpenseForm.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(loadSingleExpenseHistory.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadSingleExpenseHistory.fulfilled, (state, action) => {
				state.loading = false;
				state.expenseHistory = action.payload ?? null;
			})
			.addCase(loadSingleExpenseHistory.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message;
			})
			.addCase(loadExpenses.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadExpenses.fulfilled, (state, action) => {
				state.loading = false;
				state.list = Array.isArray(action.payload) ? action.payload : [];
				state.total = state.list.length;
			})
			.addCase(loadExpenses.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load expenses";
			})
			.addCase(saveExpense.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveExpense.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(saveExpense.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save expense";
			})
			.addCase(expenseUpdateAction.pending, (state) => {
				state.loading = true;
			})
			.addCase(expenseUpdateAction.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(expenseUpdateAction.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Expense action failed";
			})
			.addCase(loadExpensesByReporting.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadExpensesByReporting.fulfilled, (state, action) => {
				state.loading = false;
				state.expensesByReporting = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadExpensesByReporting.rejected, (state, action) => {
				state.loading = false;
				state.expensesByReporting = [];
				state.error = action.payload?.message || "Unable to load expenses by reporting";
			})
			.addCase(loadExpensesById.pending, (state) => {
				state.loading = true;
			})
			.addCase(loadExpensesById.fulfilled, (state, action) => {
				state.loading = false;
				state.expensesById = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadExpensesById.rejected, (state, action) => {
				state.loading = false;
				state.expensesById = [];
				state.error = action.payload?.message || "Unable to load expenses by employee";
			})
			.addCase(loadExpenseStatusMaster.pending, (state) => {
				state.expenseStatusLoading = true;
			})
			.addCase(loadExpenseStatusMaster.fulfilled, (state, action) => {
				state.expenseStatusLoading = false;
				state.expenseStatusMaster = Array.isArray(action.payload) ? action.payload : [];
			})
			.addCase(loadExpenseStatusMaster.rejected, (state, action) => {
				state.expenseStatusLoading = false;
				state.expenseStatusMaster = [];
				state.error = action.payload?.message || "Unable to load expense status master";
			});
	},
});

export default expenseSlice.reducer;
export const { clearExpenseForm } = expenseSlice.actions;
